import { HTMLProps, ReactNode, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { ApexOptions } from 'apexcharts';
import ApexChart, { Props as ApexChartProps } from 'react-apexcharts';

// Components
import { U21NoData } from 'app/shared/u21-ui/components/display/U21NoData';

// Utils
import {
  convertStyleObjectToCSSString,
  getDOMProps,
} from 'app/shared/utils/react';
import { useTheme } from '@mui/material/styles';
import useId from '@mui/utils/useId';
import { alpha } from '@mui/system/colorManipulator';
import { U21Loading } from 'app/shared/u21-ui/components/display/U21Loading';

export interface U21ChartProps extends HTMLProps<HTMLDivElement> {
  chartID?: string;
  height?: string | number;
  loading?: boolean;
  noDataComponent?: ReactNode;
  options?: ApexOptions;
  series: ApexChartProps['series'];
  type?: ApexChartProps['type'];
  width?: string | number;
}

const DEFAULT_OPTIONS: ApexOptions = {};
const DEFAULT_SERIES = [];

export const U21Chart = ({
  chartID: chartIDProp,
  height = 400,
  loading,
  options: optionsProp = DEFAULT_OPTIONS,
  series = DEFAULT_SERIES,
  type = 'line',
  width = '100%',
  noDataComponent = <StyledU21NoData $height={height} $width={width} />,
  ...rest
}: U21ChartProps) => {
  const chartID = useId(chartIDProp);
  const theme = useTheme();
  const options = useMemo(
    () =>
      ({
        colors: [theme.palette.primary.main],
        ...optionsProp,
        chart: {
          background: 'transparent',
          ...optionsProp.chart,
          fontFamily: theme.typography.fontFamily,
          id: chartID,
          zoom: {
            ...optionsProp.chart?.zoom,
            allowMouseWheelZoom: false,
          },
          events: {
            ...optionsProp.chart?.events,
            // call resize after mount to get the latest width and height
            // https://github.com/apexcharts/react-apexcharts/issues/187#issuecomment-901969532
            mounted: (chart) => {
              optionsProp.chart?.events?.mounted?.(chart);
              chart.windowResizeHandler();
            },
          },
        },
        dataLabels: {
          ...optionsProp.dataLabels,
          formatter: (val, opts) => {
            const formattedVal = optionsProp.dataLabels?.formatter
              ? optionsProp.dataLabels.formatter(val, opts)
              : val;

            // don't show 0 by default;
            if (formattedVal === 0) {
              return '';
            }
            return formattedVal;
          },
        },
        theme: {
          ...optionsProp.theme,
          mode: theme.palette.mode,
        },
      }) satisfies ApexOptions,
    [chartID, optionsProp, theme],
  );

  // series can be an array of numbers or an array of objects which contain a data array
  if (
    !loading &&
    (!series.length ||
      series.every((s) => typeof s === 'object' && !s.data?.length))
  ) {
    if (typeof noDataComponent === 'string') {
      return <U21NoData>{noDataComponent}</U21NoData>;
    }
    // fragment needed since typescript expects ReactElement return type
    return <>{noDataComponent}</>;
  }

  return (
    <ChartContainer $width={width} $height={height} {...getDOMProps(rest)}>
      {loading && (
        <LoadingOverlay>
          <U21Loading loading variant="spinner" size={50} label="" />
        </LoadingOverlay>
      )}
      <StyledApexChart
        key={type} // forcing re-render when type changes
        height="100%"
        options={options}
        series={series}
        type={type}
        width="100%"
      />
    </ChartContainer>
  );
};

interface StyleProps {
  $width?: number | string;
  $height?: number | string;
}

const HEIGHT_WIDTH_STYLE = css<StyleProps>`
  ${({ $height }) => {
    if (typeof $height === 'number') {
      return css`
        height: ${$height}px;
      `;
    } else if ($height) {
      return css`
        height: ${$height};
      `;
    }
    return css``;
  }}

  ${({ $width }) => {
    if (typeof $width === 'number') {
      return css`
        width: ${$width}px;
      `;
    } else if ($width) {
      return css`
        width: ${$width};
      `;
    }
    return css``;
  }}
`;

const StyledU21NoData = styled(U21NoData)<StyleProps>`
  justify-content: center;

  ${HEIGHT_WIDTH_STYLE}
`;

const ChartContainer = styled.div<StyleProps>`
  position: relative;

  ${HEIGHT_WIDTH_STYLE}
`;

const StyledApexChart = styled(ApexChart)`
  .apexcharts-xaxis-label,
  .apexcharts-yaxis-label {
    ${(props) => convertStyleObjectToCSSString(props.theme.typography.caption)}
  }

  .apx-legend-position-left,
  .apx-legend-position-right {
    max-width: 33%;
    overflow-x: hidden;
  }

  .apexcharts-legend-text {
    // important needed since apexcharts injects these with style attribute
    color: ${(props) => props.theme.palette.text.primary} !important;
    font-size: ${(props) => props.theme.typography.caption.fontSize} !important;
    font-weight: ${(props) =>
      props.theme.typography.caption.fontWeight} !important;
    flex: 1;
    word-break: break-word;
  }

  .apexcharts-xaxis-title-text,
  .apexcharts-yaxis-title-text {
    ${(props) =>
      convertStyleObjectToCSSString(props.theme.typography.subtitle2)}
  }

  &&& .apexcharts-tooltip {
    background-color: ${(props) => props.theme.palette.background.default};
    border: 1px solid ${(props) => props.theme.palette.grey[500_24]};
    box-shadow: ${(props) => props.theme.customShadows.z12};
    border-radius: 4px;

    .apexcharts-tooltip-text {
      // important needed since apexcharts injects these with style attribute
      font-size: ${(props) =>
        props.theme.typography.caption.fontSize} !important;
      font-weight: ${(props) =>
        props.theme.typography.caption.fontWeight} !important;
    }

    .apexcharts-tooltip-title {
      // important needed since apexcharts injects these with style attribute
      background: ${(props) => props.theme.palette.grey[500_16]};
      border-bottom: 1px solid ${(props) => props.theme.palette.grey[500_24]};
      font-size: ${(props) =>
        props.theme.typography.subtitle2.fontSize} !important;
      font-weight: ${(props) =>
        props.theme.typography.subtitle2.fontWeight} !important;
    }
  }

  .apexcharts-tooltip-rangebar {
    font-size: ${(props) => props.theme.typography.caption.fontSize};
    font-weight: ${(props) => props.theme.typography.caption.fontWeight};

    .series-name {
      font-size: ${(props) => props.theme.typography.subtitle2.fontSize};
      font-weight: ${(props) => props.theme.typography.subtitle2.fontWeight};
    }
  }

  .apexcharts-xaxis-annotation-label {
    font-size: ${(props) => props.theme.typography.caption.fontSize};
    font-weight: ${(props) => props.theme.typography.caption.fontWeight};
  }

  .apexcharts-menu {
    border-radius: ${(props) => props.theme.shape.borderRadius}px;
    right: 0;
    min-width: unset;
    box-shadow: ${(props) => props.theme.customShadows.z12};
  }

  .apexcharts-menu-item {
    ${(props) => convertStyleObjectToCSSString(props.theme.typography.caption)}
    border-radius: ${(props) => props.theme.shape.borderRadius}px;
    white-space: nowrap;
    &:hover {
      background-color: ${(props) => props.theme.palette.action.hover};
    }
  }

  .apexcharts-toolbar {
    max-width: unset;
  }

  .apexcharts-menu-icon,
  .apexcharts-pan-icon,
  .apexcharts-reset-icon,
  .apexcharts-selection-icon,
  .apexcharts-toolbar-custom-icon,
  .apexcharts-zoom-icon,
  .apexcharts-zoomin-icon,
  .apexcharts-zoomout-icon {
    display: flex;
    width: unset;
    height: unset;
    transform: none;
    border-radius: 50%;
    &:hover {
      background-color: ${(props) => props.theme.palette.action.hover};
    }
    & > svg {
      width: 28px;
      height: 28px;
      padding: 5px;
    }

    &.apexcharts-selected {
      background-color: ${(props) =>
        alpha(props.theme.palette.primary.main, 0.08)};
      &:hover {
        background-color: ${(props) =>
          alpha(props.theme.palette.primary.main, 0.16)};
      }
    }
  }

  .apexcharts-menu-icon,
  .apexcharts-reset-icon,
  .apexcharts-selection-icon,
  .apexcharts-toolbar-custom-icon,
  .apexcharts-zoomin-icon,
  .apexcharts-zoomout-icon {
    & > svg {
      fill: ${(props) => props.theme.palette.text.primary};
      &:hover {
        fill: ${(props) => props.theme.palette.text.primary};
      }
    }
  }

  .apexcharts-zoomout-icon {
    margin-right: 8px;
  }

  .apexcharts-reset-icon {
    margin-left: 8px;
  }

  .apexcharts-zoom-icon.apexcharts-selected svg {
    fill: ${(props) => props.theme.palette.primary.main};
  }

  .apexcharts-pan-icon.apexcharts-selected svg {
    stroke: ${(props) => props.theme.palette.primary.main};
  }

  .apexcharts-theme-light {
    .apexcharts-menu {
      background: ${(props) => props.theme.palette.background.paper};
    }
  }

  .apexcharts-theme-dark {
    .apexcharts-menu {
      background: ${(props) => props.theme.palette.background.paper};
    }
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: ${(props) => props.theme.palette.background.paper};
  opacity: 0.5;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
`;
